'use client'
import {ENDPOINTS} from "@/app/endpoints/wpEndpoints";
import {FormEvent, useState} from "react";
import {useTracking} from "@/app/contexts/tracking-context";
import {ArticleTrackingParams} from "@/app/utils/article-tracking";
import AppButton from "@/app/components/atomic/molecules/AppButton";

const FIELD_CLASSNAME = "w-full rounded-2xl bg-white px-4 ring-1 ring-inset ring-neutral focus:outline-none focus:ring-rm-gray";

interface FormData {
    name: string;
    email: string;
    message: string;
}

export function PageCommentForm({wpPageId, articleParams}: { wpPageId: string, articleParams?: ArticleTrackingParams }) {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        message: '',
    });

    const [emailError, setEmailError] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const {track} = useTracking();

    const handleChange = (inputReturn: any) => {
        setFormData({...formData, [inputReturn.fieldName]: inputReturn.value});
    };

    const validateEmail = (email: string): boolean => {
        const re = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return re.test(String(email).toLowerCase());
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitting) return;

        if (formData.email && !validateEmail(formData.email)) {
            setEmailError('Votre email est invalide');
            return;
        } else {
            setEmailError('');
            setHasError(false);
            setIsSubmitted(false);
            await postNewComment();
        }
    };

    const postNewComment = async () => {
        setIsSubmitting(true);
        try {
            const response = await fetch(ENDPOINTS.WP_CACHE_COMMENTS.POST_NEW_COMMENT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add any necessary authentication headers here
                },
                body: JSON.stringify({
                    pageId: wpPageId,
                    name: formData.name,
                    email: formData.email,
                    message: formData.message,
                }),
            });

            if (response.ok) {
                setIsSubmitted(true);
                setFormData({
                    name: '',
                    email: '',
                    message: '',
                });

                track({event: 'submit_commentaire', ...articleParams})
            } else {
                setHasError(true);
            }
        } catch (error) {
            setHasError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 rounded-3xl bg-neutral p-6 text-pale">
            <div className="flex flex-col gap-1">
                <label htmlFor="message" className="title-look text-2xl">
                    Posez votre question
                </label>
                <span className="text-rm-gray">Un expert Réassurez-moi vous répondra.</span>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col">
                    <label className="sr-only" htmlFor="name">Prénom</label>
                    <input
                        id="name"
                        name="name"
                        className={`${FIELD_CLASSNAME} h-12`}
                        placeholder="Prénom"
                        value={formData.name}
                        onChange={(e) => handleChange({value: e.target.value, fieldName: 'name'})}
                        required
                    />
                </div>

                <div className="flex flex-col">
                    <label className="sr-only" htmlFor="email">E-mail (facultatif, non publié)</label>
                    <input
                        id="email"
                        name="email"
                        type="email"
                        className={`${FIELD_CLASSNAME} h-12`}
                        placeholder="E-mail (facultatif, non publié)"
                        value={formData.email}
                        onChange={(e) => handleChange({value: e.target.value, fieldName: 'email'})}
                    />
                    {emailError && <p className="text-red-500">{emailError}</p>}
                </div>
            </div>

            <textarea
                className={`${FIELD_CLASSNAME} h-36 resize-y py-3.5`}
                id="message"
                name="message"
                placeholder="Votre commentaire ou question"
                value={formData.message}
                onChange={(e) => handleChange({value: e.target.value, fieldName: 'message'})}
                required
            />

            <AppButton type="submit" wFull disabled={isSubmitting} loading={isSubmitting}>
                Publier
            </AppButton>

            <span className="text-xs text-rm-gray">
                En publiant, vous acceptez que votre prénom et votre message soient affichés. Aucune donnée revendue.
            </span>

            {isSubmitted &&
                <div className="text-green-500 font-semibold mx-auto">
                    Votre commentaire a été envoyé avec succès !
                </div>
            }

            {hasError &&
                <div className="text-red-500 font-semibold mx-auto">
                    Une erreur est survenue, veuillez réessayer plus tard.
                </div>
            }
        </form>
    );
}
